import { inject, Injectable } from '@angular/core';
import { AccountType, SubscriptionPlan } from '@ee/common/enums';
import type { PostHogConfig } from 'posthog-js';
import { dropNoiseExceptions } from './analytics-exception-filter';
import { redactAnalyticsUrls } from './analytics-url-redaction';
import { PosthogService } from './posthog.service';

/**
 * Vendor-neutral analytics facade — the ONE service apps inject for analytics. It wraps the PostHog SDK via the
 * internal SSR-safe {@link PosthogService} so components never import `posthog-js` directly, which keeps the
 * vendor swappable, the surface testable (mock this service), and gives a single chokepoint for PII masking.
 *
 * Do NOT inject `PosthogService` from components — go through this facade.
 */
export enum Events {
  // Legacy coarse categories (kept for existing recordEvent callers; now routed to PostHog).
  REGISTRATION = 'registration',
  AUTH = 'auth',
  EVICTION = 'eviction',
  GENERAL = 'general',

  // Auth / onboarding
  REGISTRATION_STARTED = 'registration_started',
  REGISTRATION_COMPLETED = 'registration_completed',
  ACCOUNT_ACTIVATED = 'account_activated',
  LOGGED_IN = 'logged_in',
  MFA_SETUP_INITIATED = 'mfa_setup_initiated',
  MFA_COMPLETED = 'mfa_completed',
  USER_AGREEMENT_ACCEPTED = 'user_agreement_accepted',

  // Case lifecycle (PM)
  CASE_TYPE_SELECTED = 'case_type_selected',
  CASE_CREATED = 'case_created',
  CASE_STEP_COMPLETED = 'case_step_completed',
  CASE_SUBMITTED_TO_ATTORNEY = 'case_submitted_to_attorney',
  CASE_CLOSED = 'case_closed',
  CASE_FILE_UPLOADED = 'case_file_uploaded',
  DOCUMENT_GENERATED = 'document_generated',

  // Eviction (attorney)
  EVICTION_CREATED = 'eviction_created',
  EVICTION_SUBMITTED = 'eviction_submitted',
  EVICTION_STATUS_CHANGED = 'eviction_status_changed',
  DOCUMENT_DOWNLOADED = 'document_downloaded',

  // Billing
  PLAN_SELECTED = 'plan_selected',
  SUBSCRIPTION_CHANGED = 'subscription_changed',
  INVOICE_CREATED = 'invoice_created',
  INVOICE_SENT = 'invoice_sent',
  INVOICE_PAID = 'invoice_paid',
  INVOICE_REMINDER_SENT = 'invoice_reminder_sent',

  // Engagement
  MESSAGE_SENT = 'message_sent',
  TASK_COMPLETED = 'task_completed',
  CONTACT_CREATED = 'contact_created',
  WORKFLOW_PUBLISHED = 'workflow_published',
  REFERRAL_SENT = 'referral_sent',
  INTAKE_LANDING_VIEWED = 'intake_landing_viewed',
  INTAKE_FORM_SUBMITTED = 'intake_form_submitted',

  // Guidance (walkthroughs / hints)
  WALKTHROUGH_STEP_VIEWED = 'walkthrough_step_viewed',
  WALKTHROUGH_COMPLETED = 'walkthrough_completed',
  WALKTHROUGH_ABANDONED = 'walkthrough_abandoned',
  WALKTHROUGH_STEP_TARGET_MISSING = 'walkthrough_step_target_missing',
  HINT_VIEWED = 'hint_viewed',

  // Help center
  HELP_ARTICLE_VIEWED = 'help_article_viewed',
  HELP_FEEDBACK_SUBMITTED = 'help_feedback_submitted'
}

export enum RegistrationType {
  NEW_ORGANIZATION = 'New Organization',
  NEW_USER = 'New User'
}

export enum RegistrationSteps {
  PLAN_SELECTION = 'Plan Selection',
  CONFIRMATION = 'Confirmation'
}

export interface BaseProps {
  accountType: AccountType;
}

export interface GeneralProps extends BaseProps {
  event: string;
}

export interface RegistrationProps extends BaseProps {
  type: RegistrationType;
  step: RegistrationSteps;
  plan?: SubscriptionPlan;
}

export interface EvictionProps extends BaseProps {
  action: string;
  caseId: string;
  companyName: string;
}

export type EventProps = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly posthogService = inject(PosthogService);

  /**
   * Initialize the SDK once (called at app bootstrap). Applies privacy-safe defaults that back up the
   * Privacy Policy's commitments; callers may still override any of them:
   *
   *  - `respect_dnt` — honors the browser's Do Not Track signal (posthog-js defaults this to `false`).
   *    Note this is DNT only: posthog-js has no Global Privacy Control support at all — it reads
   *    `navigator.doNotTrack`/`msDoNotTrack` and nothing else. This is DELIBERATELY STRICTER than the
   *    Privacy Policy discloses: §16 ("Do Not Track and Global Privacy Control") tells readers we do not
   *    respond to DNT. Over-delivering on a protection is safe, and leaving §16 as published keeps the
   *    option to turn this off later without a policy amendment (which, post-cutover, would cost a new
   *    policy version key and re-gate every user). Do NOT "fix" this flag to match the policy sentence.
   *  - `disable_session_recording` — session replay is not disclosed in the Privacy Policy at all, so it
   *    must not be silently switched on. Replay can be enabled REMOTELY from PostHog project settings, so
   *    this client-side flag is the only thing keeping an undisclosed capture channel off without a deploy.
   *    Enabling it requires a policy amendment (i.e. a new policy version) as well as a code change.
   *  - `mask_all_text` / `mask_all_element_attributes` — autocapture stays on (heatmaps and click maps are
   *    wanted) but must not carry Customer Data. Privacy Policy §8 commits that analytics captures "account
   *    and product-use events rather than document contents or other identifiable case, tenant, SCRA, or
   *    Customer Data", and case/eviction pages hold tenant names, addresses and amounts in the DOM, which
   *    autocapture would otherwise ship as `$el_text` and element attributes. What remains is the element
   *    chain (tag/class/position) — structure, not content.
   *  - `before_send` — a two-hook chain, order-significant:
   *    {@link redactAnalyticsUrls} strips opaque ids from URL properties (together with the masking above
   *    this is what keeps a session from being linkable to one specific case), then
   *    {@link dropNoiseExceptions} discards third-party `$exception` noise. Redaction runs FIRST so that
   *    anything surviving the filter has already been scrubbed.
   *
   * Known, deliberate residual: `capture_dead_clicks` and `capture_heatmaps` are left unset, which means
   * they resolve from PostHog REMOTE CONFIG. A project-settings toggle can therefore widen DOM-derived
   * capture without a deploy. That is accepted in exchange for keeping heatmaps available — but the masking
   * above applies to those paths too, and if that trade stops being acceptable, pin both to `false` here.
   */
  init(apiKey: string, options: Partial<PostHogConfig>): void {
    this.posthogService.init(apiKey, {
      respect_dnt: true,
      disable_session_recording: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      before_send: [redactAnalyticsUrls, dropNoiseExceptions],
      ...options,
    });
  }

  /** Capture an event. Accepts an {@link Events} value or a raw string name. */
  capture(event: Events | string, props?: EventProps): void {
    this.posthogService.posthog.capture(event, props);
  }

  /** Identify the current user and set their person properties. */
  identify(distinctId: string, props?: EventProps): void {
    this.posthogService.posthog.identify(distinctId, props);
  }

  /** Set super-properties attached to every subsequent event (e.g. account_type, plan, org_id, app_version). */
  register(props: EventProps): void {
    this.posthogService.posthog.register(props);
  }

  /**
   * Associate the current user with a group (e.g. their organization) and set the group's properties, so
   * PostHog Group Analytics can aggregate/segment events and metrics by organization. Call on login.
   */
  group(groupType: string, groupKey: string, properties?: EventProps): void {
    this.posthogService.posthog.group(groupType, groupKey, properties);
  }

  /** Clear identity + super-properties (call on logout). */
  reset(): void {
    this.posthogService.posthog.reset();
  }

  /** Feature-flag check — used for tour gating and staged rollout. */
  isFeatureEnabled(flag: string): boolean {
    return this.posthogService.posthog.isFeatureEnabled(flag) ?? false;
  }

  /**
   * Register a callback fired when feature flags first load and whenever they change. Flags are fetched
   * asynchronously after init, so flag-gated UI must re-evaluate on this rather than reading once on boot.
   * Returns an unsubscribe function — callers with a finite lifecycle (e.g. a component) MUST call it on
   * teardown, since posthog-js otherwise retains the callback forever (repeated mounts leak listeners).
   */
  onFeatureFlags(callback: () => void): () => void {
    // posthog-js returns a teardown function, but the SSR no-op proxy returns undefined and a future SDK change
    // could return something else — always hand back a callable so a caller's cleanup can't throw.
    const unsubscribe = this.posthogService.posthog.onFeatureFlags(callback);
    return typeof unsubscribe === 'function' ? unsubscribe : () => undefined;
  }

  /** Force a fresh fetch of feature flags from PostHog (flags are otherwise cached and not re-fetched every
   *  page load). Used on the login page so an operator toggling `app-status` takes effect promptly. */
  reloadFeatureFlags(): void {
    this.posthogService.posthog.reloadFeatureFlags();
  }

  /** Returns a feature flag's JSON payload (e.g. the outage/maintenance `app-status` message), or null. */
  getFeatureFlagPayload<T = unknown>(flag: string): T | null {
    return (this.posthogService.posthog.getFeatureFlagPayload(flag) as T) ?? null;
  }

  // --- Back-compat with existing call sites (previously routed to Plausible, now PostHog) ---

  recordEvent(event: Events, props?: BaseProps): void {
    this.capture(event, props as unknown as EventProps);
  }

  track(name: string): void {
    this.capture('element_clicked', { name });
  }
}
